import { faCheckCircle } from '@fortawesome/pro-duotone-svg-icons/faCheckCircle';
import { Box } from '@osmosis/react-ui/components/Box';
import { Flex } from '@osmosis/react-ui/components/Box';
import { Icon } from '@osmosis/react-ui/components/Icon';
import { P } from '@osmosis/react-ui/components/Text';
import { Tooltip } from '@osmosis/tailwind-ui/components/tooltip/Tooltip';
import * as motion from 'motion/react-m';
import { useEffect, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { xpTypeValues } from '../../../../../constants';
import { useQuizContext } from '../../../../../context/QuizContext';
import { XpTypeMap } from '../../../../../types/quiz';
import ProgressBar from '../../../../ProgressBar';

const variants = {
  open: { display: 'block', scale: 1 },
  closed: { display: 'none', scale: 0.5 },
};

const timerTooltipId = 'timer-tooltip-id';
const plus = '+';
const seconds = 's';

/**
 * A timer component that displays a countdown timer and progress bar.
 * @param {number} defaultTime - The default time limit for the timer, in seconds.
 */
export const Timer = ({ defaultTime = 60 }) => {
  const { currentItem, handleUpdateXp } = useQuizContext();
  const intl = useIntl();
  const [timer, setTimer] = useState(0);

  useEffect(() => {
    if (timer >= defaultTime + 2) {
      currentItem?.nodeID &&
        handleUpdateXp(currentItem?.nodeID, XpTypeMap.EXTRA);
      return () => null;
    }

    const timeoutId = setTimeout(() => {
      setTimer((prev) => prev + 1);
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [timer]);

  return (
    <Box>
      <Tooltip
        id={timerTooltipId}
        content={intl.formatMessage({
          id: 'explanation.timer.tooltip',
          defaultMessage:
            'Take a minute to read through the takeaway and explanations to earn an extra XP.',
        })}
      />

      <Flex height="35px">
        <motion.div
          data-testid="timer-checkmark"
          animate={timer > defaultTime ? 'open' : 'closed'}
          variants={variants}
        >
          <Box bg="basicGroupBg" p={1} sx={{ borderRadius: '100px' }}>
            <Icon
              beat={timer === defaultTime + 1}
              size="xl"
              color="positive"
              icon={faCheckCircle}
            />

            <P ml={1} mr={2} as="span">
              <P fontWeight="bold" as="span" color="positiveText" mr={2}>
                {plus}
                {xpTypeValues[XpTypeMap.EXTRA]}
              </P>
              <P fontWeight="bold" as="span" color="positive">
                <FormattedMessage
                  id="dashbord.leaderboard.XP"
                  defaultMessage="XP"
                />
              </P>
            </P>
          </Box>
        </motion.div>
        {timer <= defaultTime && (
          <Flex
            alignItems="center"
            data-testid={timerTooltipId}
            data-tooltip-id={timerTooltipId}
            width="100%"
          >
            <P fontWeight="bold" color="gray50" as="span" mr={3}>
              {timer === defaultTime ? defaultTime : timer}
              {seconds}
            </P>
            <ProgressBar
              progressTransition={{ duration: 1.5 }}
              containerColor="#d1d5db"
              progress={timer / defaultTime}
              barColor="green"
              barColor2="gray30"
            />
          </Flex>
        )}
      </Flex>
    </Box>
  );
};
